import { Group } from "@fairfleet/geotab";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import Autocomplete from "@mui/material/Autocomplete";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import useTheme from "@mui/material/styles/useTheme";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";
import { useAddInState } from "../AddInStateProvider";
import { useGroups } from "@/hooks/groups/useGroups";
import { RememberedGroups } from "@/hooks/userState/useRememberedGroups";

interface Props {
  /** From useGroupFilterMemory, owned by the page header so the restore runs while this is unmounted. */
  memory: RememberedGroups<Group>;
}

export function GroupSelect({ memory: { remember, isReady, setRemember, recordSelection } }: Props) {
  const { t } = useTranslation();
  const { data: allGroups } = useGroups();
  const { groups, updateGroups } = useAddInState();
  const theme = useTheme();

  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      sx={{ width: "100%", minWidth: "300px", maxWidth: "440px" }}
    >
      <Autocomplete
        id="group-search"
        size="small"
        multiple
        value={groups ?? []}
        onChange={(_, value) => {
          updateGroups(value);
          recordSelection(value);
        }}
        sx={{
          flex: 1,
          backgroundColor: theme.palette.primary.main,
          "& .MuiAutocomplete-popupIndicator": {
            color: "white",
          },
          "& .MuiInputBase-input": { color: "white" },
          "& .MuiChip-label": {
            color: "white",
          },
          "& .MuiChip-deleteIcon": {
            color: "white",
          },
          "& .MuiAutocomplete-clearIndicator": {
            color: "white",
          },
          "& .MuiChip-root, & .MuiAutocomplete-tag": {
            color: "white",
          },
          "& .MuiAutocomplete-tag.MuiTypography-root": {
            color: "white",
          },
          "& .MuiOutlinedInput-root": {
            "& fieldset": { borderColor: theme.palette.primary.main },
            "&:hover fieldset": { borderColor: theme.palette.primary.main },
            "&.Mui-focused fieldset": { borderColor: theme.palette.primary.main },
          },
        }}
        options={allGroups ?? []}
        limitTags={1}
        getOptionKey={option => option.id}
        getOptionLabel={option => option.name ?? ""}
        filterSelectedOptions
        disableCloseOnSelect
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={params => (
          <TextField {...params} placeholder={t("faultOverview.groupSearchPlaceholder")} />
        )}
      />
      <Tooltip
        title={
          remember
            ? t("groupFilter.forget")
            : t("groupFilter.remember")
        }
      >
        <span>
          <IconButton
            size="small"
            onClick={() => setRemember(!remember)}
            disabled={!isReady}
            aria-pressed={remember}
            sx={{ color: "white" }}
          >
            {remember ? <PushPinIcon fontSize="small" /> : <PushPinOutlinedIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );
}
